import { Controller } from "@hotwired/stimulus";
import * as pdfjsLib from "pdfjs-dist";
import { SIGNER_COLORS } from "./dynamic_signers_controller";

export default class extends Controller {
  static targets = [
    "fileInput",
    "previewContainer",
    "canvas",
    "fieldsData",
    "pageInfo",
    "overlay",
    "signerSelect",
    "samePlaceCheckbox",
    "placementInstruction",
  ];

  static values = {
    signerCount: { type: Number, default: 1 },
    samePlace: { type: Boolean, default: false },
  };

  initialize() {
    this.fields = [];
    this.currentPage = 1;
    this.totalPages = 0;
    this.pdfDoc = null;
    this.dragState = null;
  }

  initWorker() {
    if (this.workerInitialized) return;
    this.workerInitialized = true;

    pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";
  }

  async loadFile() {
    const file = this.fileInputTarget.files[0];
    if (!file || file.type !== "application/pdf") return;

    this.initWorker();

    const arrayBuffer = await file.arrayBuffer();
    this.pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    this.totalPages = this.pdfDoc.numPages;
    this.currentPage = 1;

    this.previewContainerTarget.classList.remove("d-none");
    // Wait for DOM reflow so container has dimensions before rendering
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await this.renderPage(this.currentPage);
    this.updatePageInfo();
  }

  async renderPage(pageNum) {
    const page = await this.pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.0 });

    const containerWidth = this.canvasTarget.parentElement.offsetWidth;
    const scale = containerWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    this.canvasTarget.width = scaledViewport.width;
    this.canvasTarget.height = scaledViewport.height;

    const ctx = this.canvasTarget.getContext("2d");
    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;

    this.overlayTarget.style.width = `${scaledViewport.width}px`;
    this.overlayTarget.style.height = `${scaledViewport.height}px`;

    this.renderFields();
  }

  addField(event) {
    event.preventDefault();
    this._addFieldOfType("signature");
  }

  addDateField(event) {
    event.preventDefault();
    this._addFieldOfType("date");
  }

  toggleSamePlace() {
    const checked = this.hasSamePlaceCheckboxTarget && this.samePlaceCheckboxTarget.checked;
    if (checked) {
      this.fields = [];
      this.updateFieldsData();
      this.renderFields();
    }
    this.samePlaceValue = checked;
  }

  samePlaceValueChanged() {
    if (this.hasSignerSelectTarget) {
      this.signerSelectTarget.style.display = this.samePlaceValue ? "none" : "";
    }
    if (this.hasPlacementInstructionTarget) {
      this.placementInstructionTarget.textContent = this.samePlaceValue
        ? "Place shared signature and date fields — every signer will sign in these same spots."
        : "Click \"Add Signature Field\" to place signature areas on the document. Drag to reposition.";
    }
  }

  _addFieldOfType(type) {
    let signerIndex = 0;
    if (!this.samePlaceValue) {
      if (this.hasSignerSelectTarget) {
        signerIndex = parseInt(this.signerSelectTarget.value, 10) || 0;
      } else {
        signerIndex = this.fields.length % this.signerCountValue;
      }
    }

    const isDate = type === "date";
    const field = {
      page: this.currentPage,
      x: 10,
      y: isDate ? 76 : 70,
      width: isDate ? 10 : 22,
      height: isDate ? 2.5 : 4,
      signer_index: signerIndex,
      type: type,
    };

    this.fields.push(field);
    this.updateFieldsData();
    this.renderFields();
  }

  signerCountValueChanged() {
    this.updateSignerSelect();
    this.renderFields();
  }

  updateSignerSelect() {
    if (!this.hasSignerSelectTarget) return;
    const select = this.signerSelectTarget;
    const currentVal = select.value;
    select.innerHTML = "";

    for (let i = 0; i < this.signerCountValue; i++) {
      const color = SIGNER_COLORS[i % SIGNER_COLORS.length];
      const option = document.createElement("option");
      option.value = i;
      option.textContent = `Signer ${i + 1}`;
      option.style.color = color.border;
      select.appendChild(option);
    }

    if (parseInt(currentVal, 10) < this.signerCountValue) {
      select.value = currentVal;
    }
  }

  renderFields() {
    this.overlayTarget
      .querySelectorAll(".signature-field")
      .forEach((el) => el.remove());

    const pageFields = this.fields.filter(
      (f) => f.page === this.currentPage,
    );
    const width = this.overlayTarget.offsetWidth;
    const height = this.overlayTarget.offsetHeight;

    pageFields.forEach((field, index) => {
      const color =
        SIGNER_COLORS[field.signer_index % SIGNER_COLORS.length] ||
        SIGNER_COLORS[0];
      const el = document.createElement("div");
      el.className = "signature-field";
      el.style.cssText = `
        position: absolute;
        left: ${(field.x / 100) * width}px;
        top: ${(field.y / 100) * height}px;
        width: ${(field.width / 100) * width}px;
        height: ${(field.height / 100) * height}px;
        border: 2px dashed ${color.border};
        background: ${color.bg};
        cursor: move;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
        color: ${color.label};
        user-select: none;
        z-index: 10;
      `;
      const isDate = field.type === "date";
      el.textContent = this.samePlaceValue
        ? (isDate ? "Date (all)" : "Sign (all)")
        : (isDate ? `Date ${field.signer_index + 1}` : `Signer ${field.signer_index + 1}`);
      el.dataset.fieldIndex = this.fields.indexOf(field);

      el.addEventListener("mousedown", (e) => this.startDrag(e, field));

      const removeBtn = document.createElement("button");
      removeBtn.textContent = "\u00d7";
      removeBtn.style.cssText = `
        position: absolute; top: -8px; right: -8px;
        width: 20px; height: 20px; border-radius: 50%;
        background: #dc3545; color: white; border: none;
        font-size: 14px; line-height: 1; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
      `;
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeField(field);
      });
      el.appendChild(removeBtn);

      this.overlayTarget.appendChild(el);
    });
  }

  startDrag(event, field) {
    event.preventDefault();
    const rect = this.overlayTarget.getBoundingClientRect();
    const width = this.overlayTarget.offsetWidth;
    const height = this.overlayTarget.offsetHeight;

    this.dragState = {
      field,
      startX: event.clientX,
      startY: event.clientY,
      origX: field.x,
      origY: field.y,
      containerWidth: width,
      containerHeight: height,
    };

    const onMouseMove = (e) => {
      const dx =
        ((e.clientX - this.dragState.startX) /
          this.dragState.containerWidth) *
        100;
      const dy =
        ((e.clientY - this.dragState.startY) /
          this.dragState.containerHeight) *
        100;

      this.dragState.field.x = Math.max(
        0,
        Math.min(100 - this.dragState.field.width, this.dragState.origX + dx),
      );
      this.dragState.field.y = Math.max(
        0,
        Math.min(
          100 - this.dragState.field.height,
          this.dragState.origY + dy,
        ),
      );

      this.renderFields();
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      this.dragState = null;
      this.updateFieldsData();
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  removeField(field) {
    const index = this.fields.indexOf(field);
    if (index > -1) {
      this.fields.splice(index, 1);
      this.updateFieldsData();
      this.renderFields();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.renderPage(this.currentPage);
      this.updatePageInfo();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.renderPage(this.currentPage);
      this.updatePageInfo();
    }
  }

  updatePageInfo() {
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = `${this.currentPage} of ${this.totalPages}`;
    }
  }

  updateFieldsData() {
    if (this.hasFieldsDataTarget) {
      this.fieldsDataTarget.value = JSON.stringify(this.fields);
    }
  }
}
